import { Component } from "react";
import { Button } from "./ui/button";
import { AlertTriangle } from "lucide-react";

/**
 * Catches any render-time exception inside the Working Memory training
 * flow and shows a friendly recovery screen instead of a white page.
 */
class TrainingErrorBoundary extends Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface the error so a user can screenshot the console + tell us.
    // eslint-disable-next-line no-console
    console.error("[Training crash]", error, info);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
    if (this.props.onReset) this.props.onReset();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="training-error-boundary">
        <div className="max-w-md text-center space-y-4">
          <div className="w-14 h-14 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center mx-auto">
            <AlertTriangle className="w-7 h-7 text-orange-400" />
          </div>
          <h2 className="font-heading text-2xl font-bold">Something went sideways</h2>
          <p className="text-sm text-muted-foreground">
            The training session hit a snag. Your progress has been saved.
            Try starting a new session — and let us know what you were doing when it happened.
          </p>
          {this.state.error?.message && (
            <pre className="text-[10px] text-muted-foreground/60 font-mono bg-muted/40 p-2 rounded overflow-x-auto text-left">
              {this.state.error.message}
            </pre>
          )}
          <div className="flex gap-2 justify-center">
            <Button onClick={this.reset} data-testid="training-error-reset">Start a new session</Button>
            <Button variant="ghost" onClick={() => (window.location.href = "/dashboard")}>Dashboard</Button>
          </div>
        </div>
      </div>
    );
  }
}

export default TrainingErrorBoundary;
